import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Download, CheckCircle, Clock, Search, Filter, Mail, Phone, Calendar, MapPin, User } from 'lucide-react';
import axios from 'axios';

import { API_BASE } from "../utils/apiBase";

const API_URL = API_BASE;

const AdminOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, paid_orders: 0, pending_orders: 0, total_revenue: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sendingEmail, setSendingEmail] = useState(null);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/orders`);
      setOrders(response.data.orders || []);
      setStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const sendEmail = async (orderId) => {
    setSendingEmail(orderId);
    try {
      await axios.post(`${API_URL}/api/admin/send-email/${orderId}`);
      alert('Email sent successfully!');
    } catch (error) {
      alert('Failed to send email: ' + error.message);
    }
    setSendingEmail(null);
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = 
      order.customer_info?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_info?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const exportToCSV = () => {
    const headers = ['Order ID', 'Name', 'Email', 'WhatsApp', 'DOB', 'TOB', 'POB', 'Gender', 'Language', 'Status', 'Amount', 'Created'];
    const rows = filteredOrders.map(order => [
      order.id,
      order.customer_info?.full_name,
      order.customer_info?.email,
      order.customer_info?.whatsapp_number,
      order.customer_info?.date_of_birth,
      order.customer_info?.time_of_birth,
      order.customer_info?.place_of_birth,
      order.customer_info?.gender,
      order.customer_info?.language,
      order.status,
      order.amount / 100,
      new Date(order.created_at).toLocaleString()
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `karma-kundali-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-orange-600 text-white py-6 px-4 shadow-2xl">
        <div className="container mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/')} 
                className="p-2 hover:bg-white/20 rounded-xl transition-all duration-300 backdrop-blur-sm"
                data-testid="back-button"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                  <span className="text-4xl">🕉️</span>
                  <span>Karma Kundali Admin</span>
                </h1>
                <p className="text-white/80 text-sm mt-1">Customer Orders Dashboard</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchOrders}
                data-testid="refresh-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-white/20 rounded-xl hover:bg-white/30 transition-all duration-300 backdrop-blur-sm font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={exportToCSV}
                data-testid="export-button"
                className="flex items-center gap-2 px-4 py-2.5 bg-green-500 rounded-xl hover:bg-green-600 transition-all duration-300 font-medium shadow-lg"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-section">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 shadow-xl">
            <p className="text-gray-400 text-sm font-medium">Total Orders</p>
            <p className="text-4xl font-black text-white mt-1">{stats.total_orders}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 backdrop-blur-xl rounded-2xl p-5 border border-green-500/20 shadow-xl">
            <p className="text-green-300 text-sm font-medium">✓ Paid Orders</p>
            <p className="text-4xl font-black text-green-400 mt-1">{stats.paid_orders}</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-500/20 to-amber-600/10 backdrop-blur-xl rounded-2xl p-5 border border-yellow-500/20 shadow-xl">
            <p className="text-yellow-300 text-sm font-medium">⏳ Pending</p>
            <p className="text-4xl font-black text-yellow-400 mt-1">{stats.pending_orders}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500/20 to-red-600/10 backdrop-blur-xl rounded-2xl p-5 border border-orange-500/20 shadow-xl">
            <p className="text-orange-300 text-sm font-medium">💰 Revenue</p>
            <p className="text-4xl font-black text-orange-400 mt-1">₹{stats.total_revenue / 100}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-4 mb-6 flex flex-wrap gap-4 border border-white/10">
          <div className="flex-1 min-w-[250px]">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or order ID..."
                data-testid="search-input"
                className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 text-white placeholder-gray-400 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              data-testid="filter-select"
              className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl focus:border-orange-500 focus:outline-none text-white cursor-pointer"
            >
              <option value="all" className="bg-gray-800">All Status</option>
              <option value="paid" className="bg-gray-800">Paid</option>
              <option value="pending" className="bg-gray-800">Pending</option>
            </select>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-4" data-testid="orders-list">
          {loading ? (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-16 text-center border border-white/10">
              <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Loading orders...</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-16 text-center border border-white/10">
              <div className="text-6xl mb-4">📭</div>
              <p className="text-gray-400 text-lg">No orders found</p>
            </div>
          ) : (
            filteredOrders.map((order, index) => (
              <div 
                key={order.id || index} 
                className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-5 border border-white/10 hover:border-orange-500/50 transition-all duration-300 shadow-xl"
                data-testid={`order-card-${index}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  {/* Left: Customer Info */}
                  <div className="flex-1 min-w-[300px]">
                    <div className="flex items-center gap-3 mb-3">
                      {order.status === 'paid' ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-sm font-bold border border-green-500/30">
                          <CheckCircle className="w-4 h-4" />
                          PAID
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-bold border border-yellow-500/30">
                          <Clock className="w-4 h-4" />
                          PENDING
                        </span>
                      )}
                      <span className="text-orange-400 font-bold text-lg">₹{(order.amount || 0) / 100}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-orange-400" />
                      <span className="text-white text-lg font-bold">{order.customer_info?.full_name}</span>
                      <span className="text-2xl">{order.customer_info?.gender === 'male' ? '👨' : '👩'}</span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Mail className="w-4 h-4 text-blue-400" />
                        <span>{order.customer_info?.email}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Phone className="w-4 h-4 text-green-400" />
                        <span>{order.customer_info?.whatsapp_number}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar className="w-4 h-4 text-purple-400" />
                        <span>{order.customer_info?.date_of_birth} • {order.customer_info?.time_of_birth}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-300">
                        <MapPin className="w-4 h-4 text-red-400" />
                        <span>{order.customer_info?.place_of_birth}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right: Actions */}
                  <div className="flex flex-col items-end gap-2">
                    <p className="text-xs text-gray-500 font-mono bg-black/30 px-2 py-1 rounded">
                      {order.id?.substring(0, 8)}...
                    </p>
                    <p className="text-sm text-gray-400">
                      {order.created_at ? new Date(order.created_at).toLocaleString('en-IN') : '-'}
                    </p>
                    {order.status === 'paid' && (
                      <button
                        onClick={() => sendEmail(order.id)}
                        disabled={sendingEmail === order.id}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all text-sm font-medium border border-blue-500/30"
                      >
                        <Mail className="w-4 h-4" />
                        {sendingEmail === order.id ? 'Sending...' : 'Resend Email'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrdersPage;
